import { Range } from "views/components/date-range";

export type RangeTz = {
    startDate: string;
    endDate: string;
    timezone: string | null;
};

/**
 * Date utility service for formatting and manipulating dates
 */
export class DateService {
    /**
     * Creates a UTC Date object from a date string (YYYY-MM-DD format)
     * This ensures the date is interpreted as UTC midnight rather than local time
     * @param dateString - Date string in YYYY-MM-DD format
     * @returns Date object set to UTC midnight for the given date
     */
    public createUTCDate(dateString: string, isEod = false): Date {
        const [year, month, day] = dateString.split("-").map(Number);
        if (isEod) {
            return new Date(Date.UTC(year, month - 1, day, 23, 59, 59, 999));
        }
        return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
    }

    /**
     * Formats a Date object to YYYY-MM-DD hh:mm:ss format using UTC
     * @param date - The Date object to format
     * @returns Formatted date string in YYYY-MM-DD hh:mm:ss format (UTC)
     */
    public formatDateTime(date: Date): string {
        const pad = (n: number) => n.toString().padStart(2, "0");
        return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
    }

    /**
     * Parse Lucene-style range: [2025-06-10 TO 2025-06-11}^"America/New_York"
     * @param range - The Date string to format
     * @returns Object containing startDate, endDate, and timezone
     */
    public parseDateRangeString(range: string): RangeTz {
        let startDate: string = "";
        let endDate: string = "";
        let timezone: string = "";

        const matches = range.match(/\[([^\]|}]+)[\]}](?:\^"([^"]+)")?/);
        if (matches && matches[1]) {
            [startDate, endDate] = matches[1].split(" TO ");
            if (matches[2]) {
                timezone = matches[2];
            }
        }

        return {
            startDate,
            endDate,
            timezone
        };
    }

    /**
     * Parse date range object
     * @param range - Object containing startDate and endDate
     * @returns Object containing startDate and endDate strings in YYYY-MM-DD format
     */
    public parseDateRangeObject(range: Range): RangeTz {
        const startDate: string = typeof range?.startDate === "string" ? range.startDate : range?.startDate?.toISOString().slice(0, 10);

        const endDate: string = typeof range?.endDate === "string" ? range.endDate : range?.endDate?.toISOString().slice(0, 10);

        return {
            startDate,
            endDate,
            timezone: null
        };
    }

    /**
     * Parse date range
     * @param range - String or object containing startDate and endDate
     * @returns Object containing startDate and endDate strings in YYYY-MM-DD format
     */
    public parseDateRange(range: string | Range): RangeTz {
        if ("string" === typeof range) {
            return this.parseDateRangeString(range);
        } else if ("object" === typeof range) {
            return this.parseDateRangeObject(range);
        }
    }

    /**
     * Add days to date
     * @param date - Date object
     * @param dayCount - Number of days to add (can be negative)
     * @returns New Date object with days added
     */
    public addDaysToDate(date: Date, dayCount: number): Date {
        const dateCopy = new Date(date);
        dateCopy.setDate(dateCopy.getDate() + dayCount);
        return dateCopy;
    }
}
